// Compilateur — partie PÉDAGOGIE (CONTRACT §9) : sondes, conseils, outils à choix, clavier.
// Tout vient des packs ; le compilateur valide et génère les conditions `when` en vraies fonctions JS.
//
//   engine.probes   = { [nom]: { fields: { [champ]: 'number' | 'string' | 'number[]' | 'string[]' }, doc, pack } }
//   engine.advice   = [{ id, pack, key, version, on, after: [ruleId], probe, when: (facts) => bool, message, fields }]
//   engine.tools    = [{ id, pack, key, version, kind: 'factor' | 'value', target, label, rule: ruleId | null, param | null,
//                        choices: { size, sort, sources, keepExpected } }]
//   engine.keyboard = { always: [...], withMinus: [...], groups: [[...]], sort: 'asc' | 'desc', chunks, pack }

export const VERDICT_STATUSES = [
  'parse_error',
  'step',
  'goal',
  'unjustified',
  'useless',
  'trap',
  'wrong',
];
export const FIELD_TYPES = ['number', 'string', 'number[]', 'string[]', 'boolean'];
export const TOOL_SOURCES = [
  'expected',
  'own_denominator',
  'other_denominators',
  'lcm_ratio',
  'lcm',
  'numerators',
]; // genre factor (+ small:a, small:a-b)
export const VALUE_SOURCES = [
  'value',
  'traps',
  'table',
  'swap_op',
  'sign',
  'neighbors',
  'operands',
  'power',
  'letter',
]; // genre value
export const TOOL_KINDS = ['factor', 'value', 'signs', 'neutral', 'substitute', 'rewrite'];
// substitute : remplacer une lettre par une valeur (« x » par « −1 ») partout dans la ligne ; valeurs proposées par le moteur
// signs : enlever des parenthèses précédées d'un signe, l'élève choisit chaque signe
// neutral : deux opérandes voisins qui s'annulent (somme 0, produit 1), repérés par des motifs à deux opérandes
export const SPECIAL_TARGETS = ['*numeric', '*monomial', '*rewritable']; // genre value : expression sans lettre à valeur exacte ; expression qui se réduit à UN monôme (12x − 10x, 3x × 4x) ;
// genre rewrite : n'importe quelle écriture touchée — ce sont les motifs des règles qui disent si elles s'y appliquent
const EXPECTED_SOURCE = { factor: 'expected', value: 'value' };
export const SORTS = ['asc', 'desc'];
const KEY_RE = /^[a-z0-9][a-z0-9_.-]*$/i;

// ── Condition `when` : expression sur les faits d'une sonde ───────────────
// expr := ou ; ou := et ('||' et)* ; et := non ('&&' non)* ; non := '!' non | cmp
// cmp := terme (('==' | '!=' | '<' | '<=' | '>' | '>=') terme)? ; terme := nombre | "texte" | champ | '(' expr ')'
export class WhenError extends Error {}
export function compileWhen(src, fields) {
  const toks = [];
  const re =
    /\s*(?:(\|\||&&|==|!=|<=|>=|[<>!()])|(-?[0-9]+(?:[.,][0-9]+)?)|"([^"]*)"|'([^']*)'|([A-Za-z_][A-Za-z0-9_]*))/y;
  let pos = 0;
  while (pos < src.length) {
    if (/^\s*$/.test(src.slice(pos))) break;
    re.lastIndex = pos;
    const m = re.exec(src);
    if (!m) throw new WhenError(`condition illisible à partir de « ${src.slice(pos).trim()} »`);
    pos = re.lastIndex;
    if (m[1]) toks.push({ k: 'op', v: m[1] });
    else if (m[2]) toks.push({ k: 'num', v: Number(m[2].replace(',', '.')) });
    else if (m[3] !== undefined || m[4] !== undefined) toks.push({ k: 'str', v: m[3] ?? m[4] });
    else toks.push({ k: 'id', v: m[5] });
  }
  let i = 0;
  const isOp = (v) => toks[i]?.k === 'op' && toks[i].v === v;
  const used = new Set();
  // chaque sous-expression : { s: code, ty: 'number' | 'string' | 'boolean' | 'array' }
  const asBool = (e) =>
    e.ty === 'boolean' ? e.s : e.ty === 'array' ? `(${e.s}.length > 0)` : `Boolean(${e.s})`;
  function or() {
    let e = and();
    while (isOp('||')) {
      i++;
      e = { s: `${asBool(e)} || ${asBool(and())}`, ty: 'boolean' };
    }
    return e;
  }
  function and() {
    let e = not();
    while (isOp('&&')) {
      i++;
      e = { s: `${asBool(e)} && ${asBool(not())}`, ty: 'boolean' };
    }
    return e;
  }
  function not() {
    if (isOp('!')) {
      i++;
      return { s: `!${wrapB(not())}`, ty: 'boolean' };
    }
    return cmp();
  }
  const wrapB = (e) =>
    e.ty === 'boolean' && /\s/.test(e.s) && !/^\(.*\)$/.test(e.s) ? `(${e.s})` : asBool(e);
  function cmp() {
    const a = term();
    const t = toks[i];
    if (t?.k === 'op' && ['==', '!=', '<', '<=', '>', '>='].includes(t.v)) {
      i++;
      const b = term();
      if (a.ty === 'array' || b.ty === 'array')
        throw new WhenError(
          `on ne compare pas un tableau (« ${t.v} ») : utilise « distinct » ou « count »`,
        );
      if (a.ty !== b.ty)
        throw new WhenError(`comparaison « ${t.v} » entre un ${tyFr(a.ty)} et un ${tyFr(b.ty)}`);
      if (a.ty !== 'number' && !['==', '!='].includes(t.v))
        throw new WhenError(`« ${t.v} » ne compare que des nombres`);
      const op = t.v === '==' ? '===' : t.v === '!=' ? '!==' : t.v;
      return { s: `${a.s} ${op} ${b.s}`, ty: 'boolean' };
    }
    return a;
  }
  function term() {
    const t = toks[i++];
    if (!t) throw new WhenError('la condition s’arrête trop tôt');
    if (t.k === 'op' && t.v === '(') {
      const e = or();
      if (!isOp(')')) throw new WhenError('« ) » attendu dans la condition');
      i++;
      return { s: `(${e.s})`, ty: e.ty };
    }
    if (t.k === 'num') return { s: String(t.v), ty: 'number' };
    if (t.k === 'str') return { s: JSON.stringify(t.v), ty: 'string' };
    if (t.k === 'id') {
      if (t.v === 'true' || t.v === 'false') return { s: t.v, ty: 'boolean' };
      if (!Object.prototype.hasOwnProperty.call(fields, t.v)) {
        throw new WhenError(
          `champ inconnu « ${t.v} » (champs de la sonde : ${Object.keys(fields).join(', ')})`,
        );
      }
      used.add(t.v);
      const ty = fields[t.v].endsWith('[]') ? 'array' : fields[t.v];
      return { s: `facts.${t.v}`, ty };
    }
    throw new WhenError(`« ${t.v} » inattendu dans la condition`);
  }
  if (!toks.length) throw new WhenError('condition vide');
  const e = or();
  if (i < toks.length) throw new WhenError(`suite inattendue « ${toks[i].v} » dans la condition`);
  return { source: `(facts) => ${asBool(e)}`, fields: [...used] };
}
const tyFr = (t) =>
  ({ number: 'nombre', string: 'texte', boolean: 'booléen', array: 'tableau' })[t] || t;

// Champs d'un gabarit « {values} ».
export function templateFields(message) {
  return [...String(message).matchAll(/\{([^{}]*)\}/g)].map((m) => m[1].trim());
}

// ── Résolution d'une clé de règle (même pack, puis packs étendus, du plus proche au plus lointain) ──
function ancestorsOf(packs) {
  const byId = new Map(packs.map((p) => [p.id, p]));
  const memo = new Map();
  const go = (id, seen = new Set()) => {
    if (memo.has(id)) return memo.get(id);
    if (seen.has(id)) return [];
    seen.add(id);
    const out = [];
    for (const dep of byId.get(id)?.extends || []) {
      // ordre de déclaration, en largeur
      if (!out.includes(dep)) out.push(dep);
    }
    for (const dep of [...out]) for (const a of go(dep, seen)) if (!out.includes(a)) out.push(a);
    memo.set(id, out);
    return out;
  };
  return go;
}

// ── Construction ──────────────────────────────────────────────────────────
export function buildPedagogy(packs, rules, table) {
  const diagnostics = [];
  const err = (pack, rule, msg) => diagnostics.push({ level: 'error', pack, rule, msg });
  const warn = (pack, rule, msg) => diagnostics.push({ level: 'warning', pack, rule, msg });
  const ancestors = ancestorsOf(packs);
  const ruleIds = new Set(rules.map((r) => r.id));

  // règles par (pack, key) — sans les « :rev », qu'on vise explicitement par « key:rev »
  const byPackKey = new Map();
  for (const r of rules) byPackKey.set(`${r.pack}\u0000${r.key}${r.rev ? ':rev' : ''}`, r);
  const resolveRule = (pack, ref) => {
    if (typeof ref !== 'string' || !ref) return null;
    if (ref.includes('/')) {
      // « pack/key » ou id complet « pack/key@v »
      if (ruleIds.has(ref)) return rules.find((r) => r.id === ref);
      const [p, k] = ref.split('/');
      return byPackKey.get(`${p}\u0000${k}`) || null;
    }
    for (const p of [pack.id, ...ancestors(pack.id)]) {
      const r = byPackKey.get(`${p}\u0000${ref}`);
      if (r) return r;
    }
    return null;
  };

  // sondes
  const probes = {};
  for (const pack of packs) {
    for (const pr of pack.probes || []) {
      const name = typeof pr === 'string' ? pr : pr?.name;
      if (!name || !/^[a-z][a-z0-9_]*$/.test(name)) {
        err(pack.id, undefined, `sonde mal nommée : ${JSON.stringify(pr)}`);
        continue;
      }
      if (typeof pr === 'string' || !pr.fields || typeof pr.fields !== 'object') {
        err(pack.id, undefined, `la sonde « ${name} » doit déclarer ses champs (« fields »)`);
        continue;
      }
      let ok = true;
      for (const [f, ty] of Object.entries(pr.fields)) {
        if (!FIELD_TYPES.includes(ty)) {
          err(
            pack.id,
            undefined,
            `sonde « ${name} » : type « ${ty} » inconnu pour le champ « ${f} » (${FIELD_TYPES.join(', ')})`,
          );
          ok = false;
        }
      }
      if (!ok) continue;
      if (probes[name] && probes[name].pack !== pack.id)
        warn(
          pack.id,
          undefined,
          `la sonde « ${name} » redéfinit celle du pack ${probes[name].pack}`,
        );
      probes[name] = { fields: { ...pr.fields }, doc: pr.doc ?? '', pack: pack.id };
    }
  }

  // conseils
  const advice = [];
  const seen = new Set();
  for (const pack of packs) {
    for (const a of pack.advice || []) {
      const version = a.version ?? 1;
      const id = `${pack.id}/${a.key}@${version}`;
      if (!a.key || !KEY_RE.test(a.key)) {
        err(pack.id, id, 'conseil sans « key » valide');
        continue;
      }
      if (seen.has(id)) {
        err(pack.id, id, `conseil « ${id} » défini deux fois`);
        continue;
      }
      seen.add(id);
      let bad = false;
      const on = Array.isArray(a.on) ? a.on : [];
      if (!on.length) {
        err(pack.id, id, 'conseil sans statut « on » (ex. ["useless", "unjustified"])');
        bad = true;
      }
      for (const s of on)
        if (!VERDICT_STATUSES.includes(s)) {
          err(pack.id, id, `statut « ${s} » inconnu (${VERDICT_STATUSES.join(', ')})`);
          bad = true;
        }
      const after = [];
      if (a.after != null && !Array.isArray(a.after)) {
        err(pack.id, id, '« after » doit être une liste de clés de règles');
        bad = true;
      }
      for (const ref of a.after || []) {
        const r = resolveRule(pack, ref);
        if (!r) {
          err(
            pack.id,
            id,
            `« after » : règle « ${ref} » introuvable dans ${pack.id} ni dans les packs qu’il étend`,
          );
          bad = true;
        } else after.push(r.id);
      }
      const probe = probes[a.probe];
      if (!a.probe) {
        err(pack.id, id, 'conseil sans sonde (« probe »)');
        bad = true;
      } else if (!probe) {
        err(
          pack.id,
          id,
          `sonde inconnue « ${a.probe} » (connues : ${Object.keys(probes).join(', ') || 'aucune'})`,
        );
        bad = true;
      }
      if (typeof a.message !== 'string' || !a.message.trim()) {
        err(pack.id, id, 'conseil sans « message »');
        bad = true;
      }
      let whenSrc = '(facts) => true';
      if (probe) {
        if (a.when != null && String(a.when).trim()) {
          try {
            whenSrc = compileWhen(String(a.when), probe.fields).source;
          } catch (e) {
            err(pack.id, id, `condition « ${a.when} » : ${e.message}`);
            bad = true;
          }
        }
        for (const f of templateFields(a.message ?? '')) {
          if (!Object.prototype.hasOwnProperty.call(probe.fields, f)) {
            err(
              pack.id,
              id,
              `gabarit : champ inconnu « {${f}} » (champs de la sonde ${a.probe} : ${Object.keys(probe.fields).join(', ')})`,
            );
            bad = true;
          }
        }
      }
      if (bad) continue;
      advice.push({
        id,
        pack: pack.id,
        key: a.key,
        version,
        on: [...on],
        after,
        probe: a.probe,
        whenSrc,
        message: a.message,
        fields: templateFields(a.message),
        src: { when: a.when ?? null },
      });
    }
  }

  // outils à choix
  const tools = [];
  const seenT = new Set();
  for (const pack of packs) {
    for (const t of pack.tools || []) {
      const version = t.version ?? 1;
      const id = `${pack.id}/${t.key}@${version}`;
      if (!t.key || !KEY_RE.test(t.key)) {
        err(pack.id, id, 'outil sans « key » valide');
        continue;
      }
      if (seenT.has(id)) {
        err(pack.id, id, `outil « ${id} » défini deux fois`);
        continue;
      }
      seenT.add(id);
      let bad = false;
      const kind = t.kind ?? 'factor';
      if (!TOOL_KINDS.includes(kind)) {
        err(pack.id, id, `genre d’outil « ${kind} » inconnu (${TOOL_KINDS.join(', ')})`);
        continue;
      }
      if (typeof t.label !== 'string' || !t.label.trim()) {
        err(pack.id, id, 'outil sans libellé');
        bad = true;
      }
      let r = null;
      if (kind === 'factor') {
        if (!t.target || !table.nodes[t.target]) {
          err(
            pack.id,
            id,
            SPECIAL_TARGETS.includes(t.target)
              ? `la cible « ${t.target} » est réservée aux outils de genre value`
              : `cible « ${t.target} » : type de nœud inconnu`,
          );
          bad = true;
        }
        r = resolveRule(pack, t.rule);
        if (!r) {
          err(
            pack.id,
            id,
            `règle « ${t.rule} » introuvable dans ${pack.id} ni dans les packs qu’il étend`,
          );
          bad = true;
        } else if (r.kind !== 'rewrite') {
          err(pack.id, id, `la règle « ${r.id} » n’est pas une règle rewrite`);
          bad = true;
        } else if (!t.param || !(r.free || []).includes(t.param)) {
          err(
            pack.id,
            id,
            `le paramètre « ${t.param} » n’est pas une variable libre de ${r.id} (free : ${(r.free || []).join(', ') || 'aucune'})`,
          );
          bad = true;
        }
        if (r && table.nodes[t.target] && r.lhs && r.lhs.t !== 'Var' && r.lhs.t !== t.target) {
          err(
            pack.id,
            id,
            `la règle ${r.id} s’applique à un nœud ${r.lhs.t}, pas à la cible ${t.target}`,
          );
          bad = true;
        }
      } else if (kind === 'neutral') {
        // les paires viennent de règles rewrite « P(p, q) → élément neutre » : une seule source pour le moteur et l'outil
        const node = table.nodes[t.target];
        if (!node || !['add', 'mul'].includes(node.eval)) {
          err(pack.id, id, `cible « ${t.target} » : un outil neutral vise une somme ou un produit`);
          bad = true;
        }
        const want = node?.eval === 'add' ? 0 : 1;
        const pairs = [],
          ruleIds = [];
        if (!Array.isArray(t.rules) || !t.rules.length) {
          err(
            pack.id,
            id,
            '« rules » doit être une liste non vide de règles « a ∘ b → élément neutre »',
          );
          bad = true;
        }
        for (const ref of t.rules || []) {
          const rr = resolveRule(pack, ref);
          if (!rr) {
            err(
              pack.id,
              id,
              `règle « ${ref} » introuvable dans ${pack.id} ni dans les packs qu’il étend`,
            );
            bad = true;
            continue;
          }
          if (rr.kind !== 'rewrite' || !rr.lhs || rr.lhs.t !== t.target || rr.lhs.k.length !== 2) {
            err(
              pack.id,
              id,
              `la règle ${rr.id} doit être une règle rewrite dont le lhs est un ${t.target} de deux opérandes`,
            );
            bad = true;
            continue;
          }
          if (!(rr.rhs?.t === 'Num' && rr.rhs.p === want && rr.rhs.q === 1)) {
            err(
              pack.id,
              id,
              `la règle ${rr.id} doit donner ${want} (élément neutre de ${t.target})`,
            );
            bad = true;
            continue;
          }
          pairs.push(rr.lhs.k);
          ruleIds.push(rr.id);
        }
        if (bad) continue;
        tools.push({
          id,
          pack: pack.id,
          key: t.key,
          version,
          kind,
          target: t.target,
          label: t.label,
          rule: null,
          param: null,
          choices: null,
          identity: String(want),
          rules: ruleIds,
          pairs,
        });
        continue;
      } else if (kind === 'substitute') {
        // cible « *letter » : une lettre touchée dans la ligne ; les valeurs viennent du contexte (tableau, énoncé)
        if (t.target !== '*letter') {
          err(pack.id, id, 'un outil substitute vise « *letter » (une lettre de la ligne)');
          bad = true;
        }
        if (!t.label) {
          err(pack.id, id, '« label » obligatoire');
          bad = true;
        }
        if (bad) continue;
        tools.push({
          id,
          pack: pack.id,
          key: t.key,
          version,
          kind,
          target: '*letter',
          label: t.label,
          rule: null,
          param: null,
          choices: null,
        });
        continue;
      } else if (kind === 'rewrite') {
        // cible « *rewritable » : une écriture touchée dans la ligne ; les propositions sont les rhs des règles
        // du geste (justes) et des pièges de même motif — rien n'est inventé par l'outil
        if (t.target !== '*rewritable') {
          err(
            pack.id,
            id,
            'un outil rewrite vise « *rewritable » (une écriture touchée dans la ligne)',
          );
          bad = true;
        }
        if (typeof t.gesture !== 'string' || !t.gesture.trim()) {
          err(pack.id, id, '« gesture » obligatoire : le geste dont les règles sont proposées');
          bad = true;
        } else if (!rules.some((r) => r.gesture === t.gesture))
          err(pack.id, id, `aucune règle ne porte le geste « ${t.gesture} »`);
        const size = t.choices?.size ?? 4;
        if (!Number.isInteger(size) || size < 2 || size > 8) {
          err(pack.id, id, `« choices.size » ${size} hors de 2..8`);
          bad = true;
        }
        for (const f of ['rule', 'param'])
          if (t[f] != null)
            warn(pack.id, id, `« ${f} » ignoré : un outil rewrite propose les règles de son geste`);
        if (bad) continue;
        tools.push({
          id,
          pack: pack.id,
          key: t.key,
          version,
          kind,
          target: t.target,
          label: t.label,
          gesture: t.gesture,
          rule: null,
          param: null,
          choices: { size, sort: 'asc', sources: ['rules', 'traps'], keepExpected: true },
        });
        continue;
      } else if (kind === 'signs') {
        // pas de règle ni de choix : l'outil ouvre une case de signe vide par terme, le juge tranche ensuite
        if (!t.target || !table.nodes[t.target]) {
          err(pack.id, id, `cible « ${t.target} » : type de nœud inconnu`);
          bad = true;
        }
        for (const f of ['rule', 'param', 'choices'])
          if (t[f] != null)
            warn(
              pack.id,
              id,
              `« ${f} » ignoré : un outil de genre signs ne propose rien, l’élève choisit chaque signe`,
            );
        if (bad) continue;
        tools.push({
          id,
          pack: pack.id,
          key: t.key,
          version,
          kind,
          target: t.target,
          label: t.label,
          rule: null,
          param: null,
          choices: null,
        });
        continue;
      } else {
        if (!SPECIAL_TARGETS.includes(t.target) && !table.nodes[t.target]) {
          err(
            pack.id,
            id,
            `cible « ${t.target} » : ni type de nœud connu ni cible spéciale (${SPECIAL_TARGETS.join(', ')})`,
          );
          bad = true;
        }
        if (t.rule != null)
          warn(pack.id, id, '« rule » ignoré : un outil de genre value n’applique pas de règle');
        if (t.param != null)
          warn(pack.id, id, '« param » ignoré : un outil de genre value n’a pas de paramètre');
      }
      const c = t.choices || {};
      if (!Number.isInteger(c.size) || c.size < 1 || c.size > 8) {
        err(pack.id, id, `« choices.size » ${c.size} hors de 1..8`);
        bad = true;
      } else if (c.size < 2 && c.keepExpected)
        warn(pack.id, id, 'avec une seule valeur proposée, l’outil donne la réponse');
      const sort = c.sort ?? 'asc';
      if (!SORTS.includes(sort)) {
        err(pack.id, id, `tri « ${sort} » inconnu (${SORTS.join(', ')})`);
        bad = true;
      }
      if (!Array.isArray(c.sources) || !c.sources.length) {
        err(pack.id, id, '« choices.sources » doit être une liste non vide');
        bad = true;
      }
      for (const s of c.sources || []) {
        const err1 = checkSource(s, kind);
        if (err1) {
          err(pack.id, id, err1);
          bad = true;
        }
      }
      if (c.keepExpected != null && typeof c.keepExpected !== 'boolean') {
        err(pack.id, id, '« keepExpected » doit valoir true ou false');
        bad = true;
      }
      if (c.keepExpected && !(c.sources || []).includes(EXPECTED_SOURCE[kind]))
        warn(pack.id, id, `« keepExpected » sans la source « ${EXPECTED_SOURCE[kind]} »`);
      if (bad) continue;
      tools.push({
        id,
        pack: pack.id,
        key: t.key,
        version,
        kind,
        target: t.target,
        label: t.label,
        rule: kind === 'factor' ? r.id : null,
        param: kind === 'factor' ? t.param : null,
        choices: {
          size: c.size,
          sort,
          sources: [...c.sources],
          keepExpected: c.keepExpected ?? true,
        },
      });
    }
  }
  // deux outils de même clé dans des packs différents : toolChoices(engine, st, toolKey…) serait ambigu
  const byKey = new Map();
  for (const t of tools) {
    if (byKey.has(t.key))
      err(t.pack, t.id, `clé d’outil « ${t.key} » déjà utilisée par ${byKey.get(t.key)}`);
    else byKey.set(t.key, t.id);
  }

  // clavier
  let keyboard = null;
  for (const pack of packs) {
    const k = pack.keyboard;
    if (k == null) continue;
    if (typeof k !== 'object' || Array.isArray(k)) {
      err(pack.id, undefined, '« keyboard » doit être un objet');
      continue;
    }
    let bad = false;
    const out = { always: [], withMinus: [], sort: 'asc', ...(keyboard || {}) };
    // solve : touches toujours présentes sur une feuille « résoudre » (« ou » : une équation peut avoir plusieurs
    // solutions — la touche ne dit pas combien)
    for (const f of ['always', 'withMinus', 'solve']) {
      if (k[f] === undefined) continue;
      if (!Array.isArray(k[f])) {
        err(pack.id, undefined, `keyboard.${f} doit être une liste de touches`);
        bad = true;
        continue;
      }
      for (const s of k[f])
        if (!table.lex.symbols[s] && !table.syntax.words[s]) {
          err(pack.id, undefined, `keyboard.${f} : « ${s} » n’est pas un symbole connu`);
          bad = true;
        }
      out[f] = [...k[f]];
    }
    // groups : familles de touches montrées ENSEMBLE (ex. < > ≤ ≥) : dès qu'une touche est utile, toute la
    // famille s'affiche, sinon la touche seule souffle le geste (le sens qui change devant une inégalité)
    if (k.groups !== undefined) {
      if (!Array.isArray(k.groups) || !k.groups.every((g) => Array.isArray(g) && g.length >= 2)) {
        err(
          pack.id,
          undefined,
          'keyboard.groups doit être une liste de familles d’au moins deux touches',
        );
        bad = true;
      } else {
        for (const g of k.groups)
          for (const s of g)
            if (!table.lex.symbols[s] && !table.syntax.words[s]) {
              err(pack.id, undefined, `keyboard.groups : « ${s} » n’est pas un symbole connu`);
              bad = true;
            }
        out.groups = [...(out.groups || []), ...k.groups.map((g) => [...g])]; // familles ADDITIVES d'un pack à l'autre
      }
    }
    if (k.numbers !== undefined) {
      if (!['source', 'written', 'expected'].includes(k.numbers)) {
        err(
          pack.id,
          undefined,
          'keyboard.numbers doit valoir « source » (la ligne source et l’énoncé), « written » (toutes les lignes écrites et l’énoncé) ou « expected » (aussi ceux de la ligne attendue)',
        );
        bad = true;
      } else out.numbers = k.numbers;
    }
    if (k.frac !== undefined) {
      if (!['next', 'path'].includes(k.frac)) {
        err(
          pack.id,
          undefined,
          'keyboard.frac doit valoir « next » (ligne attendue) ou « path » (tout le corrigé)',
        );
        bad = true;
      } else out.frac = k.frac;
    }
    if (k.sort !== undefined) {
      if (!SORTS.includes(k.sort)) {
        err(pack.id, undefined, `keyboard.sort « ${k.sort} » inconnu (${SORTS.join(', ')})`);
        bad = true;
      } else out.sort = k.sort;
    }
    if (k.chunks !== undefined) {
      const ch = k.chunks;
      const okFrom =
        Array.isArray(ch?.from) &&
        ch.from.every((x) => ['source', 'start', 'last', 'lines'].includes(x));
      const okMax = Number.isInteger(ch?.max) && ch.max >= 0 && ch.max <= 20;
      if (!okFrom) {
        err(
          pack.id,
          undefined,
          'keyboard.chunks.from doit être une liste parmi « source » (la ligne choisie, par défaut la dernière), « start », « last », « lines » (toutes les lignes, la plus récente d’abord)',
        );
        bad = true;
      } else if (!okMax) {
        err(pack.id, undefined, 'keyboard.chunks.max doit être un entier de 0 à 20');
        bad = true;
      } else out.chunks = { from: [...ch.from], max: ch.max };
    }
    for (const f of Object.keys(k))
      if (
        !['always', 'withMinus', 'solve', 'sort', 'chunks', 'groups', 'frac', 'numbers'].includes(f)
      )
        warn(pack.id, undefined, `keyboard : champ « ${f} » ignoré`);
    if (bad) continue;
    keyboard = { ...out, pack: pack.id };
  }
  if (!keyboard) keyboard = { always: [], withMinus: [], sort: 'asc', pack: null };

  // affichage des verdicts « gardés sans HORA » : 'signal' (on dit pourquoi) ou 'neutral' (ligne gardée, sans commentaire)
  const DISPLAY_STATUSES = ['useless', 'unjustified'],
    DISPLAY_MODES = ['signal', 'neutral'];
  let display = { useless: 'signal', unjustified: 'signal' };
  for (const pack of packs) {
    const d = pack.display;
    if (d == null) continue;
    if (typeof d !== 'object' || Array.isArray(d)) {
      err(pack.id, undefined, '« display » doit être un objet { useless, unjustified }');
      continue;
    }
    for (const [k, v] of Object.entries(d)) {
      if (!DISPLAY_STATUSES.includes(k)) {
        warn(
          pack.id,
          undefined,
          `display : statut « ${k} » ignoré (${DISPLAY_STATUSES.join(', ')})`,
        );
        continue;
      }
      if (!DISPLAY_MODES.includes(v)) {
        err(pack.id, undefined, `display.${k} : « ${v} » inconnu (${DISPLAY_MODES.join(', ')})`);
        continue;
      }
      display = { ...display, [k]: v };
    }
  }

  return { probes, advice, tools, keyboard, display, diagnostics };
}

function checkSource(s, kind = 'factor') {
  if (typeof s !== 'string') return `source de choix invalide : ${JSON.stringify(s)}`;
  const m = /^small:(\d+)(?:-(\d+))?$/.exec(s);
  if (kind === 'value') {
    if (VALUE_SOURCES.includes(s)) return null;
    if (TOOL_SOURCES.includes(s) || m)
      return `la source « ${s} » est réservée aux outils de genre factor (genre value : ${VALUE_SOURCES.join(', ')})`;
    return `source de choix inconnue « ${s} » (genre value : ${VALUE_SOURCES.join(', ')})`;
  }
  if (VALUE_SOURCES.includes(s))
    return `la source « ${s} » est réservée aux outils de genre value (genre factor : ${[...TOOL_SOURCES, 'small:a', 'small:a-b'].join(', ')})`;
  if (TOOL_SOURCES.includes(s)) return null;
  if (m) {
    const a = +m[1],
      b = m[2] != null ? +m[2] : a;
    if (b < a) return `source « ${s} » : intervalle vide`;
    if (b - a > 50) return `source « ${s} » : intervalle trop large (50 valeurs au plus)`;
    return null;
  }
  return `source de choix inconnue « ${s} » (${[...TOOL_SOURCES, 'small:a', 'small:a-b'].join(', ')})`;
}
